import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Card, Dialog, ExerciseThumbnail, Sheet } from "../../../design-v2";
import { EXECUTION_ACTIONS } from "../domain/executionReducer";
import { defaultUnitForSet, isSetPerformed, loadPresentation } from "../domain/executionUi";
import {
  compatibleCardioGhostMinutes,
  compatiblePrescribedSet,
  effectiveExecutionProfile,
  plannedNoteForItem,
  prescribedExerciseForItem,
  prescribedTempoForItem,
} from "../domain/effectiveExecutionProfile";
import {
  prescribedConflictCopy,
  substitutionPresentation,
} from "../domain/substitution";
import { createClientUuid } from "../domain/clientIds";
import {
  firstWorkingSet,
  historyEntryForItem,
  lastBySetId,
} from "../domain/previousPerformance";
import { previousPerformanceEnabled } from "../domain/workoutPreferenceDefaults";
import { nextExecutionSetIndex } from "../domain/executionLimits";
import { MOTION, captureRects, collapseOut, expandIn, motionAllowed, playFrom } from "../motion";
import CardioItemRow from "./CardioItemRow";
import SetEntryRow from "./SetEntryRow";
import TimedSetRow from "./TimedSetRow";
import EntryHintInfo from "./EntryHintInfo";

// The info sheet's exit, matched to its CSS animation (and to the picker's).
const INFO_EXIT_MS = 180;

function NoteIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 5.5h10M5 9.5h7M5 13.5h5M16.5 11.5l3 3-5.5 5.5-3.5.5.5-3.5 5.5-5.5Z" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.5M12 7.6v.2" />
    </svg>
  );
}

function SwapIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 7h11l-3-3M17 17H6l3 3" />
    </svg>
  );
}

// An unmapped Weekly Plan slot (§15.8): a local placeholder with nothing to
// log until an exercise is chosen. No profile, no logging rows, no Skip.
function UnmappedSlotCard({ record, item, laneLabel, presentation, disabled, onSubstitute }) {
  const slot = prescribedExerciseForItem(record, item);
  const setCount = (slot?.sets || []).length;
  const reasonId = `unmapped-offline-${item.itemId}`;
  return (
    <Card
      padding="md"
      className={`lz-workout-item lz-workout-item--unmapped${laneLabel ? " lz-workout-item--grouped" : ""}`}
      data-item-id={item.itemId}
      data-slot-key={item.prescribedExerciseKey}
      data-lane={laneLabel || undefined}
    >
      <div className="lz-workout-unmapped">
        <div className="lz-workout-item__title-row">
          {laneLabel ? <span className="lz-workout-lane">{laneLabel}</span> : null}
          <h2>Choose an exercise</h2>
        </div>
        <p className="lz-workout-muted">
          This planned slot has no exercise yet. Choose one to log it
          {setCount ? ` (${setCount} ${setCount === 1 ? "set" : "sets"} planned)` : ""}.
        </p>
        {presentation.available ? (
          <div className="lz-workout-item__slot-actions">
            <button
              type="button"
              className="lz-workout-slot-action lz-workout-slot-action--primary"
              disabled={disabled || presentation.offline}
              aria-describedby={presentation.offline ? reasonId : undefined}
              onClick={() => onSubstitute?.(item)}
            >
              <SwapIcon /> Choose exercise
            </button>
            {presentation.offline ? (
              <span className="lz-workout-offline-reason" id={reasonId}>Needs a connection</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function notesHintCopy(notesHint) {
  if (notesHint === "CARRY_NOTES") return "Log distance or time in notes.";
  if (notesHint === "ROLLS_NOTES") return "Log rolls in notes.";
  return notesHint || null;
}

export default function ExecutionItemCard({
  record,
  item,
  laneLabel,
  preferences,
  disabled,
  dispatch,
  onActivateItem,
  online = false,
  onSubstitute = null,
}) {
  const presentation = substitutionPresentation(record, item, { online });
  if (presentation.kind === "placeholder") {
    return (
      <UnmappedSlotCard
        record={record}
        item={item}
        laneLabel={laneLabel}
        presentation={presentation}
        disabled={disabled}
        onSubstitute={onSubstitute}
      />
    );
  }
  return (
    <LoggableItemCard
      record={record}
      item={item}
      laneLabel={laneLabel}
      preferences={preferences}
      disabled={disabled}
      dispatch={dispatch}
      onActivateItem={onActivateItem}
      presentation={presentation}
      onSubstitute={onSubstitute}
    />
  );
}

function LoggableItemCard({
  record,
  item,
  laneLabel,
  preferences,
  disabled,
  dispatch,
  onActivateItem,
  presentation,
  onSubstitute,
}) {
  const profile = effectiveExecutionProfile(record, item);
  const prescribed = prescribedExerciseForItem(record, item);
  // This item's history entry (§13.1): the unit memory (§12.7), and, while
  // Previous performance is ON, per-set LAST plus Suggested on the first
  // working set. The setting is the live global preference (§13.10).
  const unitMemory = historyEntryForItem(record, item);
  const livePreviousPerformance = previousPerformanceEnabled(preferences);
  // Presentation only: when the setting turns OFF the guidance stays mounted
  // for the length of its collapse (motion.js), then goes. Without motion
  // (reduced motion, no Web Animations) it goes in the same render.
  const [seenPreviousPerformance, setSeenPreviousPerformance] = useState(livePreviousPerformance);
  const [guidanceLeaving, setGuidanceLeaving] = useState(false);
  if (seenPreviousPerformance !== livePreviousPerformance) {
    setSeenPreviousPerformance(livePreviousPerformance);
    setGuidanceLeaving(!livePreviousPerformance && motionAllowed());
  }
  const previousPerformance = livePreviousPerformance || guidanceLeaving;
  // Skip, presentation only: the card changes to its skipped state at once
  // (status, Unskip, footer), while the set body stays mounted, inert, for the
  // length of its collapse. One movement, straight to the final height.
  const [seenSkipped, setSeenSkipped] = useState(Boolean(item.skipped));
  const [bodyLeaving, setBodyLeaving] = useState(false);
  if (seenSkipped !== Boolean(item.skipped)) {
    setSeenSkipped(Boolean(item.skipped));
    setBodyLeaving(Boolean(item.skipped) && motionAllowed());
  }
  const bodyVisible = !item.skipped || bodyLeaving;
  const lastBySet = previousPerformance ? lastBySetId(item, unitMemory) : {};
  // Guidance acts only on an ACTIVE workout. Once FINISHED (Review, Continue
  // Editing) LAST stays as a read-only fact, but there
  // is no Suggested and no LAST-assisted entry: a completed value is never
  // changed through guidance (9D.1).
  const guidanceActs = record?.document?.status === "ACTIVE";
  const suggestedSetId = previousPerformance && guidanceActs && unitMemory.suggested
    ? firstWorkingSet(item)?.setId ?? null
    : null;
  const compactReps = profile?.measurementType === "REPS";
  const compactTimed = profile?.measurementType === "TIMED_HOLD";
  const compactCardio = profile?.measurementType === "CARDIO_MINUTES";
  const compactCard = compactReps || compactTimed || compactCardio;
  const prescriptionBacked = item.prescribedExerciseKey != null;
  const [notes, setNotes] = useState(item.notes || "");
  const committedNotesRef = useRef(item.notes || "");
  const noteFieldRef = useRef(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  // Presentation only: the info sheet stays mounted for the length of its
  // exit, like the Add Exercise / Substitute picker.
  const [infoClosing, setInfoClosing] = useState(false);
  const infoCloseTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(infoCloseTimerRef.current), []);
  function closeInfo() {
    if (infoClosing) return;
    if (!motionAllowed()) {
      setInfoOpen(false);
      return;
    }
    setInfoClosing(true);
    infoCloseTimerRef.current = setTimeout(() => {
      infoCloseTimerRef.current = null;
      setInfoClosing(false);
      setInfoOpen(false);
    }, INFO_EXIT_MS);
  }
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [firstFieldUnit, setFirstFieldUnit] = useState(null);
  // Motion (presentation only). The card's own element is reached through its
  // header; rows and tables are found inside it when they enter or leave.
  const headerRef = useRef(null);
  const cardElement = () => headerRef.current?.parentElement || null;
  const guidanceElements = () => [...(cardElement()?.querySelectorAll('[data-testid="set-guidance"]') || [])];
  const bodyElement = () => cardElement()?.querySelector(".lz-workout-reps-table__body, .lz-workout-cardio") || null;
  const addSetElement = () => cardElement()?.querySelector(".lz-workout-add-set") || null;
  const rowElement = (set) => cardElement()?.querySelector(`.lz-workout-set-row[data-set-index="${set.setIndex}"]`) || null;
  const leavingSetsRef = useRef(new Set());

  // Previous performance OFF: collapse the guidance lines, then unmount them.
  useEffect(() => {
    if (!guidanceLeaving) return undefined;
    let cancelled = false;
    const elements = guidanceElements();
    Promise.all(elements.map((element) => collapseOut(element))).then(() => {
      if (!cancelled) setGuidanceLeaving(false);
    });
    return () => {
      cancelled = true;
      elements.forEach((element) => element.getAnimations?.().forEach((animation) => animation.cancel()));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guidanceLeaving]);
  // Previous performance ON (after mount): the guidance lines grow in.
  const guidanceShownRef = useRef(livePreviousPerformance);
  useLayoutEffect(() => {
    const was = guidanceShownRef.current;
    guidanceShownRef.current = livePreviousPerformance;
    if (!livePreviousPerformance || was) return;
    guidanceElements().forEach((element) => expandIn(element));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePreviousPerformance]);

  // A set added after mount grows in; the rows below move down with it.
  const setIndexes = (item.sets || []).map((set) => set.setIndex).join(",");
  const knownSetIndexesRef = useRef(null);
  useLayoutEffect(() => {
    const known = knownSetIndexesRef.current;
    const current = new Set((item.sets || []).map((set) => set.setIndex));
    knownSetIndexesRef.current = current;
    if (!known) return;
    (item.sets || []).forEach((set) => {
      if (!known.has(set.setIndex)) expandIn(rowElement(set));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIndexes]);

  // Skip: the body collapses (and Add Set folds away) under a footer that is
  // already in its final place; then the body unmounts.
  useEffect(() => {
    if (!bodyLeaving) return undefined;
    let cancelled = false;
    const elements = [bodyElement(), addSetElement()].filter(Boolean);
    Promise.all([
      collapseOut(bodyElement(), { duration: MOTION.standard }),
      collapseOut(addSetElement(), { duration: MOTION.standard, axis: "width" }),
    ]).then(() => {
      if (!cancelled) setBodyLeaving(false);
    });
    return () => {
      cancelled = true;
      elements.forEach((element) => element.getAnimations?.().forEach((animation) => animation.cancel()));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyLeaving]);
  // Unskip: the body and Add Set grow back in the same movement.
  const skippedRef = useRef(item.skipped);
  const titleBeforeRef = useRef(null);
  useLayoutEffect(() => {
    const was = skippedRef.current;
    skippedRef.current = item.skipped;
    if (was === item.skipped) return;
    // If the title did move (a card with no hint line), it glides there.
    if (titleBeforeRef.current) playFrom(titleBeforeRef.current, { scale: false });
    titleBeforeRef.current = null;
    if (was && !item.skipped) {
      expandIn(bodyElement(), { duration: MOTION.standard });
      expandIn(addSetElement(), { duration: MOTION.standard, axis: "width" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.skipped]);

  // The row collapses first, then the set is removed. It takes no input while
  // it leaves, and a second tap on it does nothing.
  function removeSet(set) {
    const remove = () => dispatch({
      type: EXECUTION_ACTIONS.REMOVE_SET,
      itemId: item.itemId,
      setId: set.setId,
    });
    const element = rowElement(set);
    if (!element || !motionAllowed()) return remove();
    if (leavingSetsRef.current.has(set.setId)) return undefined;
    leavingSetsRef.current.add(set.setId);
    element.inert = true;
    return collapseOut(element).then(() => {
      leavingSetsRef.current.delete(set.setId);
      remove();
    });
  }

  function toggleSkipped() {
    const title = headerRef.current?.querySelector("h2");
    titleBeforeRef.current = title && motionAllowed() ? captureRects([title]) : null;
    dispatch({
      type: EXECUTION_ACTIONS.SET_ITEM_SKIPPED,
      itemId: item.itemId,
      skipped: !item.skipped,
    });
  }
  useEffect(() => {
    setNotes(item.notes || "");
    committedNotesRef.current = item.notes || "";
  }, [item.notes]);
  const rirEnabled = profile?.measurementType === "REPS" &&
    record.server?.policy?.rirInputEnabled !== false;
  const nextSetIndex = nextExecutionSetIndex(item);
  const performedWork = Boolean(item.cardio?.completed) || (item.sets || []).some((set) =>
    isSetPerformed(profile, set, rirEnabled)
  );
  const removalDestroysData = performedWork || Boolean(item.notes);
  // Until a load is stored the hint follows the unit shown on the first set's
  // field: its own relabel, else the same default the field resolves.
  const firstSet = (item.sets || [])[0] || null;
  const hintUnit = firstFieldUnit ||
    (firstSet ? defaultUnitForSet(item, firstSet, preferences, unitMemory) : null);
  const loadHint = compactReps || compactTimed
    ? loadPresentation(profile, preferences, (item.sets || []).find((set) => set.load)?.load, hintUnit).info
    : null;
  // "How to log this exercise" guidance lives behind the info action; only the
  // prescribed tempo and real warnings hold a line under the title.
  const loggingGuidance = compactCard ? [
    loadHint ? { key: "weight", title: "Weight logging", copy: loadHint } : null,
    notesHintCopy(profile?.notesHint)
      ? { key: "notes", title: "Notes", copy: notesHintCopy(profile?.notesHint) }
      : null,
  ].filter(Boolean) : [];
  const tempo = compactCard ? prescribedTempoForItem(record, item) : null;
  const plannedNote = plannedNoteForItem(record, item);
  const exerciseName = item.exerciseName || prescribed?.exerciseName || "Exercise";
  // The snapshot's reasons explain why the PRESCRIBED exercise conflicts; a
  // substitute no longer carries that warning (§15.8).
  const conflictCopy = item.origin === "PRESCRIBED"
    ? prescribedConflictCopy(prescribed?.constraintConflict)
    : null;
  const offlineReasonId = `substitute-offline-${item.itemId}`;
  // The slot action (§15.8): one Substitute, whatever the slot holds now; the
  // planned exercise is reached through the same picker. It stays in place for
  // the whole ACTIVE workout: once the slot has performed work it is locked,
  // without a message. Never louder than logging; a conflicting prescribed
  // exercise gets the stronger outline so the fix is discoverable.
  const slotActionShown = presentation.kind === "slot" && onSubstitute &&
    (presentation.available || presentation.locked);
  const slotActionButtons = slotActionShown ? (
    <>
      <button
        type="button"
        className={`lz-workout-slot-action${conflictCopy ? " lz-workout-slot-action--primary" : ""}`}
        disabled={disabled || presentation.offline || presentation.locked}
        data-locked={presentation.locked || undefined}
        aria-describedby={presentation.offline ? offlineReasonId : undefined}
        onClick={() => onSubstitute(item)}
      >
        <SwapIcon /> Substitute
      </button>
      {presentation.offline ? (
        // Visible where it explains a visible conflict; otherwise the disabled
        // action carries it as its description (the workout menu already says
        // it once for the whole screen).
        <span
          className={conflictCopy ? "lz-workout-offline-reason" : "lz-v2-visually-hidden"}
          id={offlineReasonId}
        >
          Needs a connection
        </span>
      ) : null}
    </>
  ) : null;
  const tableShowsActions = compactReps || compactTimed;

  function patchSet(set, patch) {
    return dispatch({
      type: EXECUTION_ACTIONS.COMMIT_SET,
      itemId: item.itemId,
      setId: set.setId,
      patch,
    }, performedSetSyncOptions(profile, set, patch, rirEnabled));
  }

  function addSet() {
    if (nextSetIndex == null) return undefined;
    return dispatch({
      type: EXECUTION_ACTIONS.ADD_SET,
      itemId: item.itemId,
      item,
      set: {
        setId: createClientUuid(),
        setIndex: nextSetIndex,
        origin: "ADDED",
        prescribedSetKey: null,
        setType: "WORKING",
        load: null,
        reps: null,
        seconds: null,
        rir: null,
        performedAt: null,
        lastEditedAt: new Date().toISOString(),
        enteredAfterFinish: record.document.status === "FINISHED",
      },
    });
  }

  function commitNotes(nextNotes = notes) {
    if (nextNotes === committedNotesRef.current) return undefined;
    committedNotesRef.current = nextNotes;
    return dispatch({
      type: EXECUTION_ACTIONS.COMMIT_NOTES,
      itemId: item.itemId,
      notes: nextNotes || null,
    });
  }

  // Clear is an explicit commit: the X unmounts once the draft is empty, so a
  // keyboard activation never produces a textarea blur to commit through.
  function clearNotes() {
    setNotes("");
    noteFieldRef.current?.focus();
    return commitNotes("");
  }

  function toggleNotes() {
    if (notesOpen) commitNotes();
    setNotesOpen((open) => !open);
  }

  function removeItem() {
    setConfirmRemove(false);
    return dispatch({ type: EXECUTION_ACTIONS.REMOVE_ITEM, itemId: item.itemId });
  }

  function requestRemoveItem() {
    if (removalDestroysData) {
      setConfirmRemove(true);
      return;
    }
    removeItem();
  }

  // Workout-structure actions live in the card footer, after Add Set and the
  // slot actions; the header rail holds only info and note.
  // Skip cannot be newly applied after canonical performed work (§15.4), so it
  // is not rendered then rather than shown disabled. Unskip always stays.
  const actionControl = prescriptionBacked ? (
    item.skipped || !performedWork ? (
      <button
        type="button"
        className={`lz-workout-item__structural-action${item.skipped ? " lz-workout-item__structural-action--unskip" : ""}`}
        disabled={disabled}
        aria-label={item.skipped ? "Unskip exercise" : "Skip exercise"}
        onClick={toggleSkipped}
      >
        {item.skipped ? "Unskip" : "Skip"}
      </button>
    ) : null
  ) : (
    <button
      type="button"
      className="lz-workout-item__structural-action lz-workout-item__structural-action--remove"
      disabled={disabled}
      onClick={requestRemoveItem}
      aria-label="Remove exercise"
    >
      Remove
    </button>
  );

  return (
    <Card
      padding={compactCard ? "none" : "md"}
      className={`lz-workout-item${compactReps ? " lz-workout-item--reps" : ""}${compactTimed ? " lz-workout-item--timed" : ""}${compactCardio ? " lz-workout-item--cardio" : ""}${laneLabel ? " lz-workout-item--grouped" : ""}`}
      data-item-id={item.itemId}
      data-slot-key={item.prescribedExerciseKey || undefined}
      data-measurement-type={profile?.measurementType}
      data-lane={laneLabel || undefined}
      data-skipped={item.skipped || undefined}
      onPointerDown={() => onActivateItem?.(item.itemId)}
      onFocusCapture={() => onActivateItem?.(item.itemId)}
    >
      <header ref={headerRef} className={`lz-workout-item__header${compactCard ? " lz-workout-item__header--compact" : ""}`}>
        {compactCard ? (
          <span className="lz-workout-item__thumbnail">
            <ExerciseThumbnail exerciseId={item.exerciseId} src={null} size={80} alt="" />
            {laneLabel ? <span className="lz-workout-lane lz-workout-lane--overlay">{laneLabel}</span> : null}
          </span>
        ) : null}
        <div className="lz-workout-item__identity">
          <div className="lz-workout-item__title-row">
            {laneLabel && !compactCard ? <span className="lz-workout-lane">{laneLabel}</span> : null}
            <h2>{exerciseName}</h2>
          </div>
          {/* The plan's instruction for this exercise: read-only, under the
              title and above the tempo. A substitute never shows it. */}
          {plannedNote ? <p className="lz-workout-item__planned-note">{plannedNote}</p> : null}
          {/* One secondary line under the title. Skipped: the status takes the
              tempo's place without changing the space it holds, so the title
              never moves or rewraps between the two states. */}
          {compactCard ? (
            <div className="lz-workout-item__subline" data-skipped={item.skipped || undefined}>
              {item.skipped ? <p className="lz-workout-item__status">Skipped</p> : null}
              {tempo ? (
                <p className="lz-workout-item__tempo" aria-hidden={item.skipped || undefined}>
                  Tempo · <span>{tempo}</span>
                </p>
              ) : null}
            </div>
          ) : null}
          {conflictCopy ? (
            <p className="lz-workout-item__conflict">
              <span aria-hidden="true">⚠</span> {conflictCopy}
            </p>
          ) : null}
        </div>
        <div className="lz-workout-item__header-actions">
          {loggingGuidance.length ? (
            <button
              type="button"
              className="lz-workout-item__info-action"
              aria-label="Exercise logging information"
              aria-haspopup="dialog"
              onClick={() => setInfoOpen(true)}
            >
              <InfoIcon />
            </button>
          ) : null}
          <button
            type="button"
            className="lz-workout-item__note-action"
            data-active={Boolean(item.notes) || undefined}
            disabled={disabled}
            aria-label={`${item.notes ? "Edit" : "Add"} note for ${exerciseName}`}
            aria-expanded={notesOpen}
            onClick={toggleNotes}
          >
            <NoteIcon />
            {item.notes ? <span className="lz-workout-item__note-dot" aria-hidden="true" /> : null}
          </button>
        </div>
      </header>

      <div
        className={`lz-workout-note-disclosure${notesOpen ? " is-open" : ""}`}
        aria-hidden={!notesOpen}
      >
        <div className="lz-workout-note-disclosure__inner">
          <div className="lz-workout-notes-field">
            <label htmlFor={`exercise-note-${item.itemId}`}>Exercise note</label>
            <div className="lz-workout-notes-field__editor">
              <textarea
                ref={noteFieldRef}
                id={`exercise-note-${item.itemId}`}
                value={notes}
                maxLength={500}
                rows={2}
                disabled={disabled || !notesOpen}
                tabIndex={notesOpen ? 0 : -1}
                placeholder="Add a note"
                onChange={(event) => setNotes(event.target.value)}
                onBlur={() => commitNotes()}
              />
              {notes ? (
                <button
                  type="button"
                  className="lz-workout-notes-field__clear"
                  disabled={disabled || !notesOpen}
                  aria-label="Clear exercise note"
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={clearNotes}
                >
                  <span aria-hidden="true">×</span>
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {!compactCard && item.skipped ? <p className="lz-workout-muted">Skipped by you</p> : null}

      {!item.skipped && !profile ? (
        <p className="lz-workout-error">This exercise has no execution profile and can’t be logged.</p>
      ) : null}

      {bodyVisible && profile?.measurementType === "CARDIO_MINUTES" ? (
        <CardioItemRow
          item={item}
          prescribedMinutes={compatibleCardioGhostMinutes(record, item)}
          disabled={disabled}
          onDone={(minutes) => dispatch({
            type: EXECUTION_ACTIONS.COMPLETE_CARDIO,
            itemId: item.itemId,
            minutes,
          })}
        />
      ) : null}

      {compactReps && (bodyVisible || slotActionButtons || actionControl) ? (
        <div
          className={`lz-workout-reps-table${profile.loadMode === "NONE" ? " lz-workout-reps-table--no-load" : ""}${rirEnabled ? "" : " lz-workout-reps-table--no-rir"}${item.skipped ? " lz-workout-reps-table--skipped" : ""}`}
        >
          {bodyVisible ? (
          <div className="lz-workout-reps-table__body" aria-hidden={item.skipped || undefined} inert={item.skipped ? "" : undefined}>
          <div className="lz-workout-reps-table__head" aria-hidden="true">
            <span>Set</span>
            {profile.loadMode !== "NONE" ? <span>Weight</span> : null}
            <span>Reps</span>
            {rirEnabled ? <span>RIR</span> : null}
          </div>
          <div className="lz-workout-reps-table__rows">
            {(item.sets || []).map((set, index) => {
              const target = compatiblePrescribedSet(record, item, (prescribed?.sets || []).find(
                (candidate) => candidate.setKey === set.prescribedSetKey || candidate.setIndex === set.setIndex
              ));
              return (
                <SetEntryRow
                  key={set.setId}
                  compact
                  ordinal={index + 1}
                  item={item}
                  set={set}
                  prescribed={target}
                  profile={profile}
                  preferences={preferences}
                  unitMemory={unitMemory}
                  last={lastBySet[set.setId] || null}
                  seedFromLast={guidanceActs}
                  suggested={set.setId === suggestedSetId ? unitMemory.suggested : null}
                  rirEnabled={rirEnabled}
                  disabled={disabled}
                  onFieldUnitChange={index === 0 ? setFirstFieldUnit : null}
                  onPatch={(patch) => patchSet(set, patch)}
                  onRemove={() => removeSet(set)}
                />
              );
            })}
          </div>
          </div>
          ) : null}
          <div className="lz-workout-reps-table__footer">
            {bodyVisible ? (
            <button
              type="button"
              className="lz-workout-add-set"
              disabled={disabled || nextSetIndex == null || Boolean(item.skipped)}
              onClick={addSet}
            >
              <span aria-hidden="true">+</span> Add Set
            </button>
            ) : null}
            {slotActionButtons}
            {actionControl}
          </div>
        </div>
      ) : null}

      {compactTimed && (bodyVisible || slotActionButtons || actionControl) ? (
        <div
          className={`lz-workout-reps-table lz-workout-timed-table${profile.loadMode === "NONE" ? " lz-workout-timed-table--no-load" : ""}${item.skipped ? " lz-workout-reps-table--skipped" : ""}`}
        >
          {bodyVisible ? (
          <div className="lz-workout-reps-table__body" aria-hidden={item.skipped || undefined} inert={item.skipped ? "" : undefined}>
          <div className="lz-workout-reps-table__head lz-workout-timed-table__head" aria-hidden="true">
            <span>Set</span>
            <span>Seconds</span>
            {profile.loadMode !== "NONE" ? (
              <span className="lz-workout-timed-table__weight-heading">
                Weight <small>optional</small>
              </span>
            ) : null}
          </div>
          <div className="lz-workout-reps-table__rows lz-workout-timed-table__rows">
            {(item.sets || []).map((set, index) => {
              const target = compatiblePrescribedSet(record, item, (prescribed?.sets || []).find(
                (candidate) => candidate.setKey === set.prescribedSetKey || candidate.setIndex === set.setIndex
              ));
              return (
                <TimedSetRow
                  key={set.setId}
                  compact
                  ordinal={index + 1}
                  item={item}
                  set={set}
                  prescribed={target}
                  profile={profile}
                  preferences={preferences}
                  unitMemory={unitMemory}
                  last={lastBySet[set.setId] || null}
                  disabled={disabled}
                  onPatch={(patch) => patchSet(set, patch)}
                  onRemove={() => removeSet(set)}
                />
              );
            })}
          </div>
          </div>
          ) : null}
          <div className="lz-workout-reps-table__footer lz-workout-timed-table__footer">
            {bodyVisible ? (
            <button
              type="button"
              className="lz-workout-add-set"
              disabled={disabled || nextSetIndex == null || Boolean(item.skipped)}
              onClick={addSet}
            >
              <span aria-hidden="true">+</span> Add Set
            </button>
            ) : null}
            {slotActionButtons}
            {actionControl}
          </div>
        </div>
      ) : null}

      {!compactCard ? <EntryHintInfo>{notesHintCopy(profile?.notesHint)}</EntryHintInfo> : null}

      {(slotActionButtons || actionControl) && !tableShowsActions ? (
        <div className="lz-workout-item__slot-actions">
          {slotActionButtons}
          {actionControl}
        </div>
      ) : null}

      <Sheet
        open={infoOpen && loggingGuidance.length > 0}
        onClose={closeInfo}
        title="Logging information"
        closeLabel="Close logging information"
        className={`lz-workout-logging-info-overlay${infoClosing ? " is-closing" : ""}`}
        surfaceClassName={`lz-workout-logging-info${infoClosing ? " is-closing" : ""}`}
      >
        <dl className="lz-workout-logging-info__list">
          {loggingGuidance.map((entry) => (
            <div key={entry.key}>
              <dt>{entry.title}</dt>
              <dd>{entry.copy}</dd>
            </div>
          ))}
        </dl>
      </Sheet>

      <Dialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        title="Remove exercise?"
        description="Logged data for this exercise will be removed."
        footer={(
          <div className="lz-workout-dialog-actions">
            <Button variant="ghost" onClick={() => setConfirmRemove(false)}>Keep Exercise</Button>
            <Button variant="danger" onClick={removeItem}>Remove Exercise</Button>
          </div>
        )}
      ><span /></Dialog>
    </Card>
  );
}

export function performedSetSyncOptions(profile, set, patch, rirInputEnabled = true) {
  const wasPerformed = isSetPerformed(profile, set, rirInputEnabled);
  const becomesPerformed = isSetPerformed(profile, { ...set, ...patch }, rirInputEnabled);
  return !wasPerformed && becomesPerformed ? { syncReason: "performed-set" } : undefined;
}
